<template>
  <ak-sidebar-layout size="large">
    <template #sidebar>
      <ak-box>
        <ak-form ref="detailsForm">
          <h3 class="color-grey">Edit template</h3>
          <ak-sms-details-form
            v-if="!isLoading"
            :active-account="activeAccount"
            :active-agency="activeAgency"
            :template="template"
            @update-template="onUpdateTemplate"
          />
        </ak-form>

        <ak-sms-templates-reply-stop />
      </ak-box>
    </template>

    <template #body>
      <ak-navbar class="bg-white" size="tiny" spacing="tiny">
        <ak-navbar-item grow />
        <ak-navbar-item>
          <ak-button color="success" @click.prevent="confirmTemplate(template)">
            Save Template
          </ak-button>
        </ak-navbar-item>
      </ak-navbar>

      <ak-loading
        v-model:active="isLoading"
        :is-full-page="false"
        :can-cancel="true"
      />

      <template v-if="!isLoading">
        <ak-mobile-view-port type="text" :from="template.from">
          <ak-variables-textarea
            v-model="template.body"
            :account="activeAccount._id"
            :agency="activeAgency._id"
            :rows="rows"
          />

          <ak-sms-length-warning :body="template.body" />
        </ak-mobile-view-port>
      </template>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { ref, defineComponent, onMounted } from 'vue'
import BasicContainer from '../containers/BasicContainer.vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import VariableTextarea from '@akerolabs/akero-vue/src/components/variables/VariablesTextarea.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Navbar from '@akerolabs/akero-vue/src/components/navbar/Navbar.vue'
import NavbarItem from '@akerolabs/akero-vue/src/components/navbar/NavbarItem.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import MobileViewPort from '@akerolabs/akero-vue/src/components/mobile/ViewPort.vue'
import SMSDetailsForm from './SMSDetailsForm.vue'
import SMSLengthWarning from './SMSLengthWarning.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import SMSReplyStop from './SMSReplyStopMessage.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'

import { useRouter } from '../../legacy/router'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-sms-templates-edit-template></ak-sms-templates-edit-template>
   */
  name: 'AkSmsTemplatesEditTemplate',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [BasicContainer.name]: BasicContainer,
    [SidebarLayout.name]: SidebarLayout,
    [Box.name]: Box,
    [Form.name]: Form,
    [Select.name]: Select,
    [Navbar.name]: Navbar,
    [NavbarItem.name]: NavbarItem,
    [Button.name]: Button,
    [MobileViewPort.name]: MobileViewPort,
    [VariableTextarea.name]: VariableTextarea,
    [SMSDetailsForm.name]: SMSDetailsForm,
    [SMSLengthWarning.name]: SMSLengthWarning,
    [Loading.name]: Loading,
    [SMSReplyStop.name]: SMSReplyStop,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    id: {
      default: undefined,
      type: String,
    },

    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Function}
     * @description Checks if a feature is available for the current account
     */
    featureEnabled: {
      default: undefined,
      type: Function,
    },
  },

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props) {
    const router = useRouter()

    const rows = ref('10')
    const isLoading = ref(true)
    const template = ref({})
    const detailsForm = ref(null)

    /**
     * @param {String} id
     * @returns {Promise}
     * @description Returns a promise to fetch the email template from the
     * Akero API
     */
    const fetchTemplate = async (id) => {
      return request.get(`/templates/${id}`, {
        params: {
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
        },
      })
    }

    const onUpdateTemplate = (data) => {
      template.value = data
    }

    /**
     * @param {Object} layout
     * @description Runs the validation before saving the sms template, this
     * will output an error if the validation fails
     */
    const confirmTemplate = async (data) => {
      const template = { ...data }
      if (detailsForm.value.validate(true)) {
        await updateTemplate(template)
      } else {
        ToastProgrammatic.open({
          message: 'Some fields are incomplete',
          type: 'danger',
        })
      }
    }

    const updateTemplate = async (template) => {
      request
        .put(`/templates/${template.id}`, template)
        .then(() => {
          ToastProgrammatic.open({
            message: 'Template has been updated',
            type: 'success',
          })
          router.push('smsList')
        })
        .catch((ex) => {
          console.log(ex)
          ToastProgrammatic.open({
            message: 'Error template could not be updated',
            type: 'danger',
          })
        })
    }

    onMounted(async () => {
      const res = await fetchTemplate(props.id)
      template.value = res.data.data
      isLoading.value = false
    })

    return {
      onUpdateTemplate,
      confirmTemplate,
      updateTemplate,
      detailsForm,
      isLoading,
      template,
      rows,
    }
  },
})
</script>
